import { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AtSign, Check, Ban } from "lucide-react";
import { toast } from "sonner";

/**
 * MentionToast — the "@Someone pinged you" push. Rendered at 2x the size of a
 * card broadcast, stays 2 seconds, then auto-dismisses (unless the target
 * clicks Abuse or Dismiss). Multiple pings queue; only the head is visible.
 *
 * Also owns the SSE subscription and the on-load replay of unresolved pings.
 */
export function MentionOverlay() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const shownRef = useRef(new Set());  // dedupe across the SSE + /pending fetch

  const enqueue = useCallback((ev) => {
    if (!ev || !ev.id || shownRef.current.has(ev.id)) return;
    shownRef.current.add(ev.id);
    setQueue((q) => [...q, ev]);
  }, []);

  // Initial replay + SSE stream.
  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        const { data } = await api.get("/mentions/pending");
        (data.items || []).forEach(enqueue);
      } catch { /* silently ignore — the SSE will still push new mentions */ }
    })();
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/mentions/stream`;
    const es = new EventSource(url, { withCredentials: true });
    es.onerror = () => { api.get("/auth/me").catch(() => es.close()); };
    es.onmessage = (msgEvent) => {
      try {
        const ev = JSON.parse(msgEvent.data);
        if (ev.type === "mention") enqueue(ev);
      } catch { /* malformed event — drop it */ }
    };
    return () => es.close();
  }, [user?.id, enqueue]);

  const head = queue[0];
  // Auto-dismiss after 2s if the user doesn't act. The record is left
  // unresolved on the server — a refresh will re-surface it once (soft push).
  useEffect(() => {
    if (!head) return;
    const t = setTimeout(() => setQueue((q) => q.slice(1)), 2000);
    return () => clearTimeout(t);
  }, [head]);

  const dismiss = async (ev) => {
    setQueue((q) => q.filter((x) => x.id !== ev.id));
    try { await api.post(`/mentions/${ev.id}/dismiss`); } catch { /* best-effort */ }
  };
  const abuse = async (ev) => {
    setQueue((q) => q.filter((x) => x.id !== ev.id));
    try {
      const { data } = await api.post(`/mentions/${ev.id}/abuse`);
      toast.success(`Reported. ${ev.sender_name} muted for ${data.ban_minutes} minute(s).`);
    } catch (e) {
      toast.error(apiError(e, "Couldn't report"));
    }
  };

  return (
    <div className="fixed inset-x-0 top-6 z-[70] pointer-events-none flex justify-center">
      <AnimatePresence>
        {head && (
          <motion.div
            key={head.id}
            data-testid={`mention-toast-${head.id}`}
            initial={{ opacity: 0, y: -30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className="pointer-events-auto max-w-2xl w-full mx-4 rounded-2xl border-2 border-fuchsia-400/60 bg-gradient-to-br from-fuchsia-500/25 via-purple-500/20 to-cyan-500/20 backdrop-blur-xl shadow-[0_0_45px_rgba(232,121,249,0.5)] p-6"
          >
            <div className="flex items-start gap-4">
              <div className="h-14 w-14 rounded-2xl bg-fuchsia-400/25 border-2 border-fuchsia-300/60 flex items-center justify-center shrink-0">
                <AtSign className="h-8 w-8 text-fuchsia-100" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-2xl tracking-[0.06em] text-white truncate">{head.sender_name} pinged you</div>
                <div className="mt-1 text-sm text-white/70 line-clamp-3">{head.message_text}</div>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <button
                  type="button"
                  data-testid={`mention-abuse-${head.id}`}
                  onClick={() => abuse(head)}
                  className="inline-flex items-center gap-1 rounded-lg bg-red-500/80 hover:bg-red-400 text-white px-3 py-1.5 text-xs font-semibold transition-colors"
                >
                  <Ban className="h-3.5 w-3.5" /> Abuse
                </button>
                <button
                  type="button"
                  data-testid={`mention-dismiss-${head.id}`}
                  onClick={() => dismiss(head)}
                  className="inline-flex items-center gap-1 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 px-3 py-1.5 text-xs font-medium transition-colors"
                >
                  <Check className="h-3.5 w-3.5" /> Dismiss
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
